import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { toast } from "react-toastify";

import useContract from "../../hooks/useContract";

import {
  Page,
  Panel,
  Stat,
  formatRaw,
  formatDate,
  tierLabel,
  tierStatus,
  getError,
} from "./ktlShared";

const Rewards = () => {
  const {
    address,
    isConnected,
    getUserDashboardData,
    claimReward,
    getAllClaimHistory,
  } = useContract();

  const [data, setData] =
    useState(null);

  const [history, setHistory] =
    useState([]);

  const [kindUsdPrice, setKindUsdPrice] =
    useState("0");

  const [loading, setLoading] =
    useState(false);

  const [claiming, setClaiming] =
    useState(false);

  const [error, setError] =
    useState("");

  const load = useCallback(async () => {
    if (!address) {
      setData(null);
      setHistory([]);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const [
        dashboardData,
        claimRecords,
      ] = await Promise.all([
        getUserDashboardData(
          address
        ),

        getAllClaimHistory(
          address
        ),
      ]);

      setData(
        dashboardData
      );

      setHistory(
        claimRecords || []
      );
    } catch (err) {
      console.error(
        "Rewards load error:",
        err
      );

      setError(
      
      );
    } finally {
      setLoading(false);
    }
  }, [
    address,
    getAllClaimHistory,
    getUserDashboardData,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const token =
    data?.rewardToken || {
      symbol: "KIND",
      decimals: 18,
    };

  const estimatedRewardRaw =
    data?.estimatedReward
      ?.estimatedKind || 0n;

  const claimStatus =
    data?.claimStatus || null;

  const currentTierStatus =
    tierStatus(data?.tier);

  const estimatedUsd =
    useMemo(() => {
      const tokenAmount =
        Number(
          formatRaw(
            estimatedRewardRaw,
            token.decimals,
            8
          )
        );

      const usdPrice =
        Number(
          kindUsdPrice || 0
        );

      if (
        !Number.isFinite(
          tokenAmount
        ) ||
        !Number.isFinite(
          usdPrice
        )
      ) {
        return 0;
      }

      return (
        tokenAmount *
        usdPrice
      );
    }, [
      estimatedRewardRaw,
      kindUsdPrice,
      token.decimals,
    ]);

  const currentSharePercentage =
    useMemo(() => {
      const userKp =
        Number(
          data?.kpBreakdown
            ?.totalKp || 0
        );

      const tierTotalKp =
        Number(
          data?.estimatedReward
            ?.tierTotalKp || 0
        );

      if (
        tierTotalKp <= 0 ||
        userKp <= 0
      ) {
        return 0;
      }

      return (
        (userKp /
          tierTotalKp) *
        100
      );
    }, [data]);

  const canClaim =
    Boolean(
      claimStatus &&
        claimStatus
          .claimableAmount >
          0n &&
        !claimStatus.claimed &&
        !claimStatus.expired
    );

  const handleClaim =
    async () => {
      if (!data?.currentLeagueId) {
        toast.error(
          "League information is unavailable."
        );

        return;
      }

      try {
        setClaiming(true);

        const result =
          await claimReward(
            data.currentLeagueId
          );

        toast.success(
          result?.hash
            ? `Reward claimed: ${result.hash}`
            : "Reward claimed."
        );

        await load();
      } catch (err) {
        console.error(
          "Claim error:",
          err
        );

        toast.error(
          getError(err)
        );
      } finally {
        setClaiming(false);
      }
    };

  if (!isConnected) {
    return (
      <Page
        eyebrow="KIND rewards"
        title="Rewards"
        description="Connect your wallet to view estimated and claimable rewards."
      >
        <Panel>
          <p className="text-sm text-[#888]">
            Connect your wallet to view
            your rewards.
          </p>
        </Panel>
      </Page>
    );
  }

  return (
    <Page
      eyebrow="KIND rewards"
      title="Rewards"
      description="View estimated rewards, current tier share, claimable balances and permanent claim history."
      action={
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="rounded-[8px] border border-[#ffcc66]/25 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-[#ffcc66] disabled:opacity-50"
        >
          {loading
            ? "Refreshing"
            : "Refresh"}
        </button>
      }
    >
      {error ? (
        <div className="mb-5 rounded-xl border border-red-400/20 bg-red-400/10 p-4 text-sm text-red-300">
          {error}
        </div>
      ) : null}

      <Panel className="mb-6 bg-gradient-to-br from-[#111314] to-[#29240d]">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-[#ffcc66]">
              Estimated Reward
            </p>

            <h2 className="mt-2 break-words text-4xl font-black">
              {formatRaw(
                estimatedRewardRaw,
                token.decimals,
                4
              )}{" "}
              {token.symbol}
            </h2>

            <p className="mt-2 text-sm text-[#777]">
              ≈ $
              {estimatedUsd.toLocaleString(
                "en-GB",
                {
                  maximumFractionDigits: 2,
                }
              )}
            </p>
          </div>

          <div className="flex flex-col items-start gap-3 md:items-end">
            <span
              className={`rounded-full border px-3 py-1 text-[9px] font-bold uppercase tracking-[0.13em] ${currentTierStatus.cls}`}
            >
              {
                currentTierStatus.label
              }
            </span>

            <span className="text-sm text-[#888]">
              {tierLabel(
                data?.tierId ||
                  data?.wallet?.tier
              )}
            </span>
          </div>
        </div>

        <div className="mt-6 max-w-sm">
          <label className="text-[10px] uppercase tracking-[0.14em] text-[#777]">
            Current KIND price in USD
          </label>

          <input
            type="number"
            min="0"
            step="any"
            value={kindUsdPrice}
            onChange={(event) =>
              setKindUsdPrice(
                event.target.value
              )
            }
            placeholder="0.00"
            className="mt-2 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none placeholder:text-[#555] focus:border-[#ffcc66]/40"
          />
        </div>
      </Panel>

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Claimable"
          value={`${formatRaw(
            claimStatus
              ?.claimableAmount,
            token.decimals,
            4
          )} ${token.symbol}`}
          accent
        />

        <Stat
          label="Current Share"
          value={`${currentSharePercentage.toFixed(
            4
          )}%`}
          note="Share of total KP inside your tier"
        />

        <Stat
          label="Total Rewards Earned"
          value={`${formatRaw(
            data?.wallet
              ?.totalRewardsEarned,
            token.decimals,
            4
          )} ${token.symbol}`}
        />

        <Stat
          label="Total Rewards Claimed"
          value={`${formatRaw(
            data?.wallet
              ?.totalRewardsClaimed,
            token.decimals,
            4
          )} ${token.symbol}`}
        />

        <Stat
          label="Current League KP"
          value={`${data?.kpBreakdown?.currentLeagueKp?.toString() || "0"} KP`}
        />

        <Stat
          label="Pending KP"
          value={`${data?.kpBreakdown?.totalPendingKp?.toString() || "0"} KP`}
        />

        <Stat
          label="Total KP"
          value={`${data?.kpBreakdown?.totalKp?.toString() || "0"} KP`}
        />

        <Stat
          label="Claim Expiry"
          value={formatDate(
            claimStatus?.expiresAt
          )}
          note={
            claimStatus?.expired
              ? "Claim period expired"
              : "Claim before this date"
          }
        />
      </div>

      <Panel className="mb-7">
        <div className="flex flex-col justify-between gap-5 md:flex-row md:items-center">
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-[#ffcc66]">
              Reward Claim
            </p>

            <h3 className="mt-2 text-xl font-black">
              League #
              {data?.currentLeagueId?.toString() ||
                "0"}
            </h3>

            <p className="mt-2 max-w-xl text-sm leading-6 text-[#777]">
              A reward can only be claimed once.
              Claims are protected against
              duplicate withdrawals.
            </p>
          </div>

          <button
            type="button"
            onClick={handleClaim}
            disabled={
              !canClaim ||
              claiming
            }
            className="rounded-xl bg-[#ffcc66] px-6 py-3 text-[11px] font-black uppercase tracking-[0.13em] text-[#191a1b] disabled:bg-[#333] disabled:text-[#777]"
          >
            {claiming
              ? "Claiming"
              : claimStatus?.claimed
              ? "Already Claimed"
              : claimStatus?.expired
              ? "Claim Expired"
              : canClaim
              ? `Claim ${token.symbol}`
              : "Nothing to Claim"}
          </button>
        </div>
      </Panel>

      <Panel className="overflow-hidden p-0">
        <div className="border-b border-white/[0.06] p-5">
          <p className="text-[10px] uppercase tracking-[0.16em] text-[#ffcc66]">
            Permanent Records
          </p>

          <h3 className="mt-2 text-xl font-black">
            Claim History
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-black/25 text-left text-[9px] uppercase tracking-[0.15em] text-[#777]">
              <tr>
                <th className="px-5 py-4">
                  League
                </th>

                <th className="px-5 py-4">
                  Allocated
                </th>

                <th className="px-5 py-4">
                  Claimed
                </th>

                <th className="px-5 py-4">
                  Status
                </th>

                <th className="px-5 py-4">
                  Claim Date
                </th>

                <th className="px-5 py-4">
                  Reference
                </th>
              </tr>
            </thead>

            <tbody>
              {history.length ? (
                history.map(
                  (
                    record,
                    index
                  ) => (
                    <tr
                      key={`${record.leagueId?.toString() || "0"}-${index}`}
                      className="border-t border-white/[0.05] text-sm"
                    >
                      <td className="px-5 py-4">
                        #
                        {record.leagueId?.toString() ||
                          "0"}
                      </td>

                      <td className="px-5 py-4">
                        {formatRaw(
                          record.allocated,
                          token.decimals,
                          4
                        )}{" "}
                        {
                          token.symbol
                        }
                      </td>

                      <td className="px-5 py-4">
                        {formatRaw(
                          record.claimed,
                          token.decimals,
                          4
                        )}{" "}
                        {
                          token.symbol
                        }
                      </td>

                      <td className="px-5 py-4">
                        {record.claimedStatus
                          ? "Claimed"
                          : "Pending"}
                      </td>

                      <td className="px-5 py-4">
                        {formatDate(
                          record.claimedAt
                        )}
                      </td>

                      <td className="px-5 py-4 font-mono text-xs text-[#888]">
                        {String(
                          record.txHash ||
                            ""
                        ).slice(
                          0,
                          14
                        )}
                        ...
                      </td>
                    </tr>
                  )
                )
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-5 py-12 text-center text-[#666]"
                  >
                    No claim records
                    found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </Page>
  );
};

export default Rewards;